import { Moment } from "moment";
import { constants } from "../constants";
import { OriginatingTransactionClassStatus } from "../types/amr-pipeline/enums/OriginatingTransactionClassStatus";
import { OriginatingTransactionClass } from '../types/amr-pipeline/models/OriginatingTransactionClass';
import { InventoryFloatingIndex } from "../types/inventory/InventoryFloatingIndex";
import { dateTimeUtils, formatUtils, moneyUtils, numericUtils } from "./";
import { constants as amrConstants } from "../components/amrPipeline/common/constants";
import { Tranche } from '../types/amr-pipeline/models/Tranche';

const TransactionClassRatings: (keyof OriginatingTransactionClass)[] = [
    'expectedRatingMoodys',
    'expectedRatingSnp',
    'expectedRatingFitch',
    'expectedRatingKbra',
    'expectedRatingDbrs',
];

const TrancheOriginalRatings: (keyof Tranche)[] = [
    'originalRatingMoodys',
    'originalRatingSnP',
    'originalRatingFitch',
    'originalRatingKbra',
    'originalRatingDbrs',
];

const TrancheCurrentRatings: (keyof Tranche)[] = [
    'ratingMoodys',
    'ratingSnP',
    'ratingFitch',
    'ratingKbra',
    'ratingDbrs',
];

function convertAmrToBwicInventoryFloaterIndex(amrFloaterIndex?: string): InventoryFloatingIndex | undefined {
    switch(amrFloaterIndex?.toUpperCase()) {
        case "FIXED":
            return InventoryFloatingIndex.Fixed;
        case "SOFR":
            return InventoryFloatingIndex.Sofr;
        case "LIBOR 1M":
            return InventoryFloatingIndex.Libor1m;
        case "LIBOR 3M":
            return InventoryFloatingIndex.Libor3m;
        case "EURIBOR 3M":
            return InventoryFloatingIndex.Euribor3m;
        case "EURIBOR 6M":
            return InventoryFloatingIndex.Euribor6m;
        case "SONIA":
            return InventoryFloatingIndex.Sonia;
        default:
            return undefined;
    }
}

function formatClassStatus(status?: OriginatingTransactionClassStatus) {
    switch (status) {
        case OriginatingTransactionClassStatus.NotOffered:
            return amrConstants.notOffered;
        case OriginatingTransactionClassStatus.Subject:
            return amrConstants.subject
        default: return;
    }
}

function formatInventoryCoupon(floaterIndex?: string, margin?: number, emptyValue = constants.emptyPlaceholder) {
    const inventoryPosition = {
        floaterIndex: convertAmrToBwicInventoryFloaterIndex(floaterIndex),
        spread: margin,
    };

    return formatUtils.formatInventoryCoupon(inventoryPosition, emptyValue);
}

function formatClassesTotalBalance(balance: number){
    return moneyUtils.money(Number(numericUtils.round(Math.abs(Math.trunc(balance) / 1.0e9), 3).toFixed(3))) + 'B';
};

function formatRollerDeadlineDate(rollerDeadlineDate: Moment) {
    return dateTimeUtils.isToday(rollerDeadlineDate)
        ? `Today ${rollerDeadlineDate.tz(constants.estTimezone).format(constants.timeFormat)}`
        : rollerDeadlineDate.tz(constants.estTimezone).format(constants.rollerDeadlineFormat);
}

function formatRatingFor<T extends Object>(transactionClass: T, fields: (keyof T)[]) {
    const [ratingMoodys, ratingSnP, ratingFitch, ratingKbra, ratingDbrs] = fields;

    return `${transactionClass[ratingMoodys] || constants.emptyPlaceholder}/${
        transactionClass[ratingSnP] || constants.emptyPlaceholder
    }/${transactionClass[ratingFitch] || constants.emptyPlaceholder}/${
        transactionClass[ratingKbra] || constants.emptyPlaceholder
    }/${transactionClass[ratingDbrs] || constants.emptyPlaceholder}`;
}

const formatKMatrixRating = (transactionClass: OriginatingTransactionClass) => {
  const [ratingMoodys, ratingSnP, ratingFitch, ratingKbra, ratingDbrs] = TransactionClassRatings;

    const ratings = [
        transactionClass[ratingMoodys],
        transactionClass[ratingSnP],
        transactionClass[ratingFitch],
        transactionClass[ratingKbra],
        transactionClass[ratingDbrs]
    ];

    // Find last non-empty rating index
    let lastRatingIndex = ratings.length - 1;
    while (lastRatingIndex >= 0 && !ratings[lastRatingIndex]) {
        lastRatingIndex--;
    }

    // If all ratings are empty, return single placeholder
    if (lastRatingIndex < 0) {
        return constants.emptyPlaceholder;
    }

    // Only include ratings up to last non-empty one
    return ratings
        .slice(0, lastRatingIndex + 1)
        .map(r => r || constants.emptyPlaceholder)
        .join('/');
}

function formatRating(transactionClass: OriginatingTransactionClass) {
    return formatRatingFor(transactionClass, TransactionClassRatings);
}

function formatOriginalRating(tranche: Tranche) {
    return formatRatingFor(tranche, TrancheOriginalRatings);
}

function formatCurrentRating(tranche: Tranche) {
    return formatRatingFor(tranche, TrancheCurrentRatings);
}

export const amrFormatUtils = {
    convertAmrToBwicInventoryFloaterIndex,
    formatInventoryCoupon,
    formatRollerDeadlineDate,
    formatRating,
    formatKMatrixRating,
    formatOriginalRating,
    formatCurrentRating,
    formatClassStatus,
    formatClassesTotalBalance
};
